/**
 * «Уведомления» — Spec 022, round-3 mockup 6, approved by Alexander.
 *
 * The main questions first: send or not, and how much — three levels with
 * examples. Quiet hours, agents, sound and digests are switches. Projects and
 * topics fold inside, so the screen is not a wall of checkboxes. Every change
 * is saved at once (the server takes partial patches), and a failure says so
 * and puts the switch back.
 */

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";

import { api } from "@/api/client";
import { Projects } from "@/api/resources";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { AppBar, Caption, EmptyNote, ScreenHeader, Switch } from "@/kit";
import { cn } from "@/lib/cn";
import { useBackButton } from "@/lib/telegram-ui";
import { zoneLabel, zoneOptions } from "@/lib/timezones";

type Mode = "all" | "progress" | "attention";

interface Preferences {
  enabled: boolean;
  mode: Mode;
  project_modes: Record<string, Mode>;
  categories: Record<string, boolean>;
  include_agent_actions: boolean;
  silent: boolean;
  daily_digest: boolean;
  weekly_digest: boolean;
  timezone: string;
  quiet_start: string | null;
  quiet_end: string | null;
  muted_until: string | null;
}
type Patch = Partial<Omit<Preferences, "project_modes">> & { project_modes?: Record<string, Mode | null> };
interface ModeOption { value: Mode; label: string; example: string }
interface Settings { preferences: Preferences; categories: Record<string, string>; modes?: ModeOption[] }

const PATH = "/api/v1/me/notifications";
const KEY = ["notification-settings"] as const;
// Same words as the bot; only if an older server sends no modes.
const FALLBACK_MODES: ModeOption[] = [
  { value: "all", label: "Всё подряд", example: "каждое действие, включая служебное" },
  { value: "progress", label: "Ход работы и важное", example: "итоги, результаты, решения, сообщения" },
  { value: "attention", label: "Только то, что ждёт тебя", example: "проверить, решить, ответить" },
];

function Row({ title, sub, trailing }: { title: ReactNode; sub?: ReactNode; trailing?: ReactNode }) {
  return (
    <li className="flex items-center gap-3 border-b border-p-border px-4 py-3 last:border-0">
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-5 text-p-text">{title}</span>
        {sub && <span className="block text-meta text-p-text-muted">{sub}</span>}
      </span>
      {trailing}
    </li>
  );
}

function Fold({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-p-border last:border-0">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 px-4 py-3 text-left press-surface">
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-5 text-p-text">{title}</span>
          <span className="block truncate text-meta text-p-text-muted">{sub}</span>
        </span>
        <ChevronDown className={cn("h-5 w-5 shrink-0 text-p-text-muted transition-transform", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && <div className="border-t border-p-border px-4 py-2">{children}</div>}
    </li>
  );
}

export function NotificationsScreen() {
  useBackButton("/me");
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: KEY, queryFn: () => api<Settings>(PATH), refetchOnMount: "always" });
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list() });
  // Once per visit: render stays pure; a pause ending mid-visit shows on the next open.
  const [now] = useState(() => Date.now());
  const save = useMutation({
    mutationFn: (patch: Patch) => api<Preferences>(PATH, { method: "PATCH", body: patch }),
    onMutate: (patch) => {
      const before = qc.getQueryData<Settings>(KEY);
      if (before) {
        const modes = { ...before.preferences.project_modes };
        for (const [id, m] of Object.entries(patch.project_modes ?? {})) { if (m) modes[id] = m; else delete modes[id]; }
        qc.setQueryData<Settings>(KEY, { ...before, preferences: { ...before.preferences, ...patch, project_modes: modes,
          categories: { ...before.preferences.categories, ...patch.categories } } });
      }
      return { before };
    },
    onSuccess: (preferences, patch) => {
      qc.setQueryData<Settings>(KEY, (s) => (s ? { ...s, preferences } : s));
      // One zone per person: the profile shows the same one (PC audit, point 13).
      if (patch.timezone) void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e: Error, _patch, ctx) => {
      if (ctx?.before) qc.setQueryData(KEY, ctx.before);
      toast.error(`Не сохранилось: ${e.message}`);
    },
  });

  const data = settings.data;
  if (settings.isLoading) return <div className="flex flex-col gap-3"><AppBar fallback="/me" context="Профиль" /><SkeletonList count={4} /></div>;
  if (!data) {
    return (
      <div className="flex flex-col gap-3">
        <AppBar fallback="/me" context="Профиль" />
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void settings.refetch()}>Повторить</Button>}>Не удалось загрузить настройки.</EmptyNote>
      </div>
    );
  }
  const p = data.preferences;
  const modes = data.modes?.length ? data.modes : FALLBACK_MODES;
  const set = (patch: Patch) => save.mutate(patch);
  const paused = p.muted_until && new Date(p.muted_until).getTime() > now;
  const liveProjects = (projects.data ?? []).filter((x) => !x.archived_at);
  const modeLabel = (m: Mode | undefined) => (m ? modes.find((x) => x.value === m)?.label.toLowerCase() : "как везде");
  const categoriesOn = Object.values(p.categories).filter(Boolean).length;
  const categoriesAll = Object.keys(data.categories).length;
  const quiet = p.quiet_start !== null;

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/me" context="Профиль" />
      <ScreenHeader title="Уведомления" />

      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        <Row title="Присылать в Telegram" sub={paused ? `пауза до ${new Date(p.muted_until!).toLocaleString("ru-RU", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}` : "бот пишет в личные сообщения"}
          trailing={<Switch on={p.enabled} label="Присылать в Telegram" onChange={() => set({ enabled: !p.enabled })} />} />
      </ul>
      {paused && <Button size="compact" variant="secondary" className="self-start" onClick={() => set({ muted_until: null, enabled: true })}>Возобновить сейчас</Button>}

      <Caption>Сколько присылать</Caption>
      <div role="radiogroup" aria-label="Сколько присылать" className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {modes.map((m) => {
          const on = p.mode === m.value;
          return (
            <button key={m.value} type="button" role="radio" aria-checked={on} onClick={() => set({ mode: m.value })}
              className="flex w-full items-start gap-3 border-b border-p-border px-4 py-3 text-left last:border-0 press-surface">
              <span className={cn("mt-0.5 h-5 w-5 shrink-0 rounded-full border-2", on ? "border-[6px] border-p-text" : "border-p-border-strong")} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold leading-5 text-p-text">{m.label}{m.value === "progress" ? " · рекомендуем" : ""}</span>
                <span className="block text-meta text-p-text-muted">{m.example}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-meta text-p-text-muted">Обращения к тебе, назначения и запросы решений приходят в любом режиме.</p>

      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        <Row title="Тихие часы" sub={quiet ? `${p.quiet_start}–${p.quiet_end ?? "08:00"} · ${zoneLabel(p.timezone)}` : "выключены"}
          trailing={<Switch on={quiet} label="Тихие часы" onChange={() => set(quiet ? { quiet_start: null, quiet_end: null } : { quiet_start: "23:00", quiet_end: "08:00" })} />} />
        {quiet && (
          <li className="flex flex-wrap items-center gap-3 border-b border-p-border px-4 py-3 text-body text-p-text">
            <label className="flex items-center gap-2">с <input type="time" aria-label="Начало тихих часов" value={p.quiet_start ?? "23:00"}
              onChange={(e) => e.target.value && set({ quiet_start: e.target.value })} className="rounded-lg border border-p-border-strong bg-p-surface px-2 py-1" /></label>
            <label className="flex items-center gap-2">до <input type="time" aria-label="Конец тихих часов" value={p.quiet_end ?? "08:00"}
              onChange={(e) => e.target.value && set({ quiet_end: e.target.value })} className="rounded-lg border border-p-border-strong bg-p-surface px-2 py-1" /></label>
          </li>
        )}
        <Row title="Что сделали агенты" sub="записи журнала и результаты агентов партнёра"
          trailing={<Switch on={p.include_agent_actions} label="Что сделали агенты" onChange={() => set({ include_agent_actions: !p.include_agent_actions })} />} />
        <Row title="Без звука" sub="сообщения приходят тихо"
          trailing={<Switch on={p.silent} label="Без звука" onChange={() => set({ silent: !p.silent })} />} />
        <Row title="Утренняя сводка" sub="что ждёт и что сделано"
          trailing={<Switch on={p.daily_digest} label="Утренняя сводка" onChange={() => set({ daily_digest: !p.daily_digest })} />} />
        <Row title="Недельная сводка" sub="раз в неделю"
          trailing={<Switch on={p.weekly_digest} label="Недельная сводка" onChange={() => set({ weekly_digest: !p.weekly_digest })} />} />
      </ul>

      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {liveProjects.length > 0 && (
          <Fold title="По проектам" sub={liveProjects.map((x) => `${x.title} — ${modeLabel(p.project_modes[x.id])}`).join(" · ")}>
            {liveProjects.map((x) => (
              <label key={x.id} className="flex min-h-11 items-center justify-between gap-3 py-1 text-body text-p-text">
                <span className="min-w-0 truncate">{x.title}</span>
                <select value={p.project_modes[x.id] ?? ""} aria-label={`Уведомления по проекту ${x.title}`}
                  onChange={(e) => set({ project_modes: { [x.id]: (e.target.value || null) as Mode | null } })}
                  className="min-h-10 rounded-lg border border-p-border-strong bg-p-surface px-2 text-body">
                  <option value="">как везде</option>
                  {modes.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
            ))}
          </Fold>
        )}
        <Fold title="Темы" sub={`${categoriesOn} из ${categoriesAll} включены`}>
          {Object.entries(data.categories).map(([key, label]) => (
            <div key={key} className="flex min-h-11 items-center justify-between gap-3 py-1 text-body text-p-text">
              <span>{label}</span>
              <Switch on={p.categories[key] ?? true} label={label} onChange={() => set({ categories: { [key]: !(p.categories[key] ?? true) } })} />
            </div>
          ))}
        </Fold>
        <Fold title="Пауза и часовой пояс" sub={`часовой пояс: ${zoneLabel(p.timezone)}`}>
          <div className="flex flex-wrap gap-2 py-2">
            <Button size="compact" variant="secondary" onClick={() => set({ muted_until: new Date(Date.now() + 3_600_000).toISOString() })}>Пауза на час</Button>
            <Button size="compact" variant="secondary" onClick={() => set({ muted_until: new Date(Date.now() + 86_400_000).toISOString() })}>На сутки</Button>
          </div>
          <div className="py-2">
            <SelectField label="Часовой пояс" value={p.timezone} options={zoneOptions([p.timezone])}
              onChange={(tz) => { if (tz && tz !== p.timezone) set({ timezone: tz }); }}
              hint="Тот же, что в профиле. Сообщения дождутся конца тихих часов или паузы." />
          </div>
        </Fold>
      </ul>
      <p className="text-meta text-p-text-muted" role="status">{save.isPending ? "Сохраняем…" : "Изменения сохраняются сразу."}</p>
    </div>
  );
}
